import React from "react";
import { CaretDown } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Generic multi-select over `{ value, label }` options.
 * `value` is an array of option values; `onChange(nextValues)`.
 */
export default function MultiSelect({ options, value, onChange, placeholder = "Select…", testId }) {
  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const selectedLabels = options.filter((o) => value.includes(o.value)).map((o) => o.label);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between font-normal text-sm h-9"
          data-testid={testId}
        >
          <span className={`truncate ${selectedLabels.length ? "text-slate-800" : "text-slate-400"}`}>
            {selectedLabels.length ? selectedLabels.join(", ") : placeholder}
          </span>
          <CaretDown size={14} className="ml-1.5 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
        {options.length === 0 && <div className="text-xs text-slate-400 p-2">No options.</div>}
        <div className="max-h-60 overflow-auto space-y-0.5">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
              data-testid={testId ? `${testId}-option-${o.value}` : undefined}
            >
              <Checkbox checked={value.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              <span className="text-sm text-slate-700 truncate">{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
